import Immutable from 'seamless-immutable';
import PropTypes from 'prop-types';
import { push, goBack } from 'connected-react-router';
import { retrieveErrors, retrieveSuccessMessage } from '@widergy/web-utils/lib/message';
import Polling from '@widergy/polling-handling';
import { AlertHandler } from '@widergy/energy-ui';
import { isEmpty } from '@widergy/web-utils/lib/array';

import { CLAIM, PROCEDURE } from 'constants/userForms';
import * as AccountService from 'services/AccountService';
import { ROUTES } from 'constants/routes';
import { appConfig } from 'config/customization';
import { message } from 'utils/stringUtils';
import { trackEvent } from 'services/AnalyticsService';
import {
  categories as analyticsCategories,
  actions as analyticsActions,
  labels as analyticsLabels
} from 'constants/analytics';
import { QUALITY } from 'constants/general';

import { actionCreators as alertActions } from './alertHandlers';
import { actionCreators as authActions } from './authHandlers';
import { actionCreators as userFormActions } from './userFormHandlers';
import { stringArrayToObject } from './utils';

/* ------------- Account actions ------------- */

export const actions = stringArrayToObject(
  [
    'ADD_ACCOUNTS',
    'ADD_ACCOUNTS_SUCCESS',
    'ADD_ACCOUNTS_FAILURE',
    'GET_ACCOUNTS',
    'GET_ACCOUNTS_SUCCESS',
    'GET_ACCOUNTS_FAILURE',
    'APPEND_ACCOUNTS',
    'APPEND_ACCOUNTS_SUCCESS',
    'APPEND_ACCOUNTS_FAILURE',
    'GET_ACCOUNT_ASYNC',
    'GET_ACCOUNT_ASYNC_FAILURE',
    'GET_ACCOUNT_ASYNC_SUCCESS',
    'GET_ACCOUNT_ALERTS',
    'GET_ACCOUNT_ALERTS_SUCCESS',
    'GET_ACCOUNT_ALERTS_FAILURE',
    'CLOSE_ACCOUNT_ALERTS',
    'GET_POSITIVE_CREDIT_STATUS',
    'GET_POSITIVE_CREDIT_STATUS_SUCCESS',
    'GET_POSITIVE_CREDIT_STATUS_FAILURE',
    'CLEAR_POSITIVE_CREDIT_STATUS',
    'CHANGE_CURRENT_ACCOUNT',
    'CLEAR_CURRENT_ACCOUNT',
    'DELETE_ACCOUNT',
    'DELETE_ACCOUNT_SUCCESS',
    'DELETE_ACCOUNT_FAILURE',
    'GET_QUESTIONS',
    'GET_QUESTIONS_SUCCESS',
    'GET_QUESTIONS_FAILURE',
    'GET_RELATED_ACCOUNTS',
    'GET_RELATED_ACCOUNTS_SUCCESS',
    'GET_RELATED_ACCOUNTS_FAILURE',
    'GET_TERMS_AND_CONDITIONS',
    'GET_TERMS_AND_CONDITIONS_SUCCESS',
    'GET_TERMS_AND_CONDITIONS_FAILURE',
    'CLEAN_ACCOUNT_NEW_NOTIFICATIONS',
    'SET_NEXT_STEP',
    'SET_RELATED_ACCOUNTS',
    'RELATED_ACCOUNT_LOADING',
    'CLEAR_SOCIAL_RATE_STATUS',
    'VALIDATE_SOCIAL_RATE',
    'VALIDATE_SOCIAL_RATE_SUCCESS',
    'VALIDATE_SOCIAL_RATE_FAILURE',
    'GET_ACCOUNT_BALANCE',
    'GET_ACCOUNT_BALANCE_SUCCESS',
    'GET_ACCOUNT_BALANCE_FAILURE',
    'RESET_ACCOUNT_BALANCE',
    'GET_TERMS_OF_SERVICE',
    'GET_TERMS_OF_SERVICE_SUCCESS',
    'GET_TERMS_OF_SERVICE_FAILURE',
    'ACCEPT_SERVICE_AGREEMENT',
    'ACCEPT_SERVICE_AGREEMENT_SUCCESS',
    'ACCEPT_SERVICE_AGREEMENT_FAILURE',
    'ADD_ACCOUNT_INIT',
    'ADD_ACCOUNT_SET_INITIAL_ACCOUNT',
    'ADD_ACCOUNT_UPDATE_STEP',
    'ADD_ACCOUNT_INITIAL_ACCOUNT_ADDED',
    'ADD_ACCOUNT_RELATED_ACCOUNTS_ADDED',
    'ADD_ACCOUNT_FINISH',
    'SHOWED_RELATED_SUPPLIES',
    'EVALUATE_CHANGE_EXP_DATE',
    'EVALUATE_CHANGE_EXP_DATE_SUCCESS',
    'EVALUATE_CHANGE_EXP_DATE_FAILURE',
    'CHANGE_EXPIRATION_DATE',
    'CHANGE_EXPIRATION_DATE_SUCCESS',
    'CHANGE_EXPIRATION_DATE_FAILURE',
    'ACCOUNT_UPDATED',
    'BILLS_UPDATED',
    'CLAIMS_UPDATED'
  ],
  '@@ACCOUNT'
);

const privateActionCreators = {
  getAccountsAsyncSuccess: payload => ({ type: actions.GET_ACCOUNTS_SUCCESS, payload }),
  getAccountsAsyncFailure: payload => ({ type: actions.GET_ACCOUNTS_FAILURE, payload }),
  getAccountAsyncSuccess: payload => ({ type: actions.GET_ACCOUNT_ASYNC_SUCCESS, payload }),
  getAccountAsyncFailure: payload => ({ type: actions.GET_ACCOUNT_ASYNC_FAILURE, payload }),
  getAccountAlertsSuccess: payload => ({
    type: actions.GET_ACCOUNT_ALERTS_SUCCESS,
    payload
  }),
  getAccountAlertsFailure: payload => ({
    type: actions.GET_ACCOUNT_ALERTS_FAILURE,
    payload
  }),
  getPositiveCreditStatusSuccess: payload => ({ type: actions.GET_POSITIVE_CREDIT_STATUS_SUCCESS, payload }),
  getPositiveCreditStatusFailure: payload => ({ type: actions.GET_POSITIVE_CREDIT_STATUS_FAILURE, payload }),
  appendAccountsSuccess: payload => ({ type: actions.APPEND_ACCOUNTS_SUCCESS, payload }),
  appendAccountsFailure: payload => ({ type: actions.APPEND_ACCOUNTS_FAILURE, payload })
};

// TODO: Refactor this into private and public actions. Delete loading action.
export const actionCreators = {
  accountUpdated: () => ({ type: actions.ACCOUNT_UPDATED }),
  billsUpdated: () => ({ type: actions.BILLS_UPDATED }),
  claimsUpdated: () => ({ type: actions.CLAIMS_UPDATED }),
  addAccount: accountNumber => async (dispatch, getState) => {
    dispatch({ type: actions.ADD_ACCOUNTS });
    const isFirstAccount = !getState().account.accounts.length;
    const { signUpInProgress } = getState().auth.signUpFlow;
    const user = getState().auth.currentUser;
    const response = await Polling.handle(AccountService.addAccount(accountNumber));
    if (response.error) {
      dispatch({ type: actions.ADD_ACCOUNTS_FAILURE });
      const error = retrieveErrors(response.error, message('DefaultMessages:addAccountFailure'));
      trackEvent(
        analyticsCategories.REGISTRACIONES,
        analyticsActions.ASOCIAR_SUMINISTRO_ERROR,
        `${analyticsLabels.ASOCIAR_SUMINISTRO_ERROR} ${error}`
      );
      AlertHandler.error(error);
    } else {
      AlertHandler.success(
        retrieveSuccessMessage(response.data, message('DefaultMessages:addAccountSuccess'))
      );
      trackEvent(
        analyticsCategories.REGISTRACIONES,
        analyticsActions.ASOCIAR_SUMINISTRO,
        analyticsLabels.ASOCIAR_SUMINISTRO_OK
      );
      dispatch(actionCreators.addAccountsSuccess());
      if (!signUpInProgress) dispatch({ type: actions.ADD_ACCOUNT_INITIAL_ACCOUNT_ADDED });
      dispatch(actionCreators.getAccountsAsync(user.id, appConfig.shouldUseAccOptimization));
      if (isFirstAccount && signUpInProgress) {
        dispatch(authActions.updateSignUpStep());
      }
    }
  },
  addAccounts: accountList => async (dispatch, getState) => {
    dispatch({ type: actions.ADD_ACCOUNTS });
    const isFirstAccount = !getState().account.accounts.length;
    const user = getState().auth.currentUser;
    const { signUpInProgress } = getState().auth.signUpFlow;
    const response = await Polling.handle(AccountService.postRelatedAccounts(accountList));
    if (response.error) {
      const error = retrieveErrors(response.error, message('DefaultMessages:addAccountsFailure'));
      dispatch({ type: actions.ADD_ACCOUNTS_FAILURE });
      trackEvent(
        analyticsCategories.REGISTRACIONES,
        analyticsActions.ASOCIAR_SUMINISTRO_RELACIONADO_ERROR,
        `${analyticsLabels.ASOCIAR_SUMINISTRO_RELACIONADO_ERROR} ${error}`
      );
      AlertHandler.error(error);
    } else {
      AlertHandler.success(
        retrieveSuccessMessage(response.data, message('DefaultMessages:addAccountsSuccess'))
      );
      dispatch(actionCreators.addAccountsSuccess());
      trackEvent(
        analyticsCategories.REGISTRACIONES,
        analyticsActions.ASOCIAR_SUMINISTRO_RELACIONADO,
        analyticsLabels.ASOCIAR_SUMINISTRO_RELACIONADO_OK
      );
      if (!signUpInProgress) dispatch({ type: actions.ADD_ACCOUNT_RELATED_ACCOUNTS_ADDED });
      dispatch(actionCreators.getAccountsAsync(user.id, appConfig.shouldUseAccOptimization));
      if (isFirstAccount) {
        dispatch(push(ROUTES.ACCOUNT_STATUS));
      } else if (!isFirstAccount && signUpInProgress) {
        if (appConfig.signUpFlow.ServiceAdherence) {
          dispatch(authActions.updateSignUpStep());
        } else {
          dispatch(push(ROUTES.ACCOUNT_STATUS));
        }
      }
    }
  },
  addAccountsSuccess: () => ({ type: actions.ADD_ACCOUNTS_SUCCESS }),
  getAccountAsync: accountId => async (dispatch, getState) => {
    dispatch({ type: actions.GET_ACCOUNT_ASYNC });
    const { currentUser } = getState().auth;
    const response = await Polling.handle(AccountService.getAccountAsync(accountId), 330000);
    if (response.error) {
      AlertHandler.error(message('DefaultMessages:getAccountAsyncFailure'));
      dispatch(privateActionCreators.getAccountAsyncFailure());
    } else if (currentUser) {
      dispatch(actionCreators.accountUpdated());
      dispatch(privateActionCreators.getAccountAsyncSuccess(response.data));
    }
  },
  getAccountAlerts: accountId => async dispatch => {
    dispatch({ type: actions.GET_ACCOUNT_ALERTS });
    const response = await Polling.handle(AccountService.getAccountAlerts(accountId));
    if (response.error) {
      dispatch(privateActionCreators.getAccountAlertsFailure(response.error));
    } else {
      dispatch(privateActionCreators.getAccountAlertsSuccess(response.data));
    }
  },

  closeAccountAlerts: () => async (dispatch, getState) => {
    const { alerts } = getState().account;
    const newAlerts = alerts.slice(1);
    dispatch({ type: actions.CLOSE_ACCOUNT_ALERTS, payload: newAlerts });
  },

  getAccountsAsync: (userId, briefData = false, pageNumber, pageSize) => async (dispatch, getState) => {
    dispatch({ type: actions.GET_ACCOUNTS });
    const { currentUser } = getState().auth;
    const response = await Polling.handle(
      AccountService.getAccountsAsync(briefData, pageNumber, pageSize),
      330000
    );
    if (response.error) {
      dispatch(actionCreators.clearCurrentAccount());

      dispatch(
        privateActionCreators.getAccountsAsyncFailure(
          retrieveErrors(response.error, message('DefaultMessages:getAccountsInfoFailure'))
        )
      );
    } else {
      const accounts = response.data;
      if (!isEmpty(accounts)) {
        const currentAccount =
          accounts.length === 1 ? accounts[0] : AccountService.getCurrentAccount(userId, accounts);
        if (currentUser) {
          dispatch(actionCreators.changeCurrentAccount(currentAccount));
          dispatch(privateActionCreators.getAccountsAsyncSuccess(accounts));
        }
      } else {
        dispatch(actionCreators.clearCurrentAccount());
        dispatch(privateActionCreators.getAccountsAsyncSuccess(accounts));
        if (appConfig.signUpFlow.AddAccount && !appConfig.canUseDashboardWithoutAccounts) {
          await dispatch(authActions.restartSignUp(6));
        }
      }
    }
  },
  getPositiveCreditStatus: (clientNumber, type) => async dispatch => {
    dispatch({ type: actions.GET_POSITIVE_CREDIT_STATUS });
    const service =
      type === QUALITY
        ? AccountService.getPositiveCreditStatusQuality
        : AccountService.getPositiveCreditStatusWarranty;

    const response = await Polling.handle(service(clientNumber));

    if (response.error) {
      AlertHandler.error(
        retrieveErrors(response.error, message('DefaultMessages:positiveCreditStatusFailure'))
      );
      dispatch(privateActionCreators.getPositiveCreditStatusFailure(response.error));
    } else {
      dispatch(privateActionCreators.getPositiveCreditStatusSuccess(response.data.message));
    }
  },
  clearPositiveCreditStatus: () => async dispatch => dispatch({ type: actions.CLEAR_POSITIVE_CREDIT_STATUS }),
  appendAccountsAsync: (pageNumber, pageSize = 20, briefData = false) => async dispatch => {
    dispatch({ type: actions.APPEND_ACCOUNTS });
    const response = await Polling.handle(
      AccountService.getAccountsAsync(briefData, pageNumber, pageSize),
      330000
    );
    if (response.error) {
      AlertHandler.error(message('DefaultMessages:getAccountAsyncFailure'));
      dispatch(privateActionCreators.appendAccountsFailure());
    } else {
      dispatch(privateActionCreators.appendAccountsSuccess(response.data));
    }
  },
  changeCurrentAccount(account) {
    return async (dispatch, getState) => {
      const { auth } = getState();
      AccountService.setCurrentAccount(auth.currentUser.id, account);
      dispatch({ type: actions.CHANGE_CURRENT_ACCOUNT, payload: { account } });
      dispatch(userFormActions.getFormCategories(CLAIM));
      dispatch(userFormActions.getFormCategories(PROCEDURE));
    };
  },
  deleteAccount: accountId => async (dispatch, getState) => {
    dispatch({ type: actions.DELETE_ACCOUNT });
    const user = getState().auth.currentUser;
    const response = await Polling.handle(AccountService.deleteAccount(accountId));
    if (response.error) {
      dispatch(actionCreators.deleteAccountFailure());
      AlertHandler.error(retrieveErrors(response.error, message('DefaultMessages:deleteAccountFailure')));
    } else {
      AlertHandler.success(
        retrieveSuccessMessage(response.data, message('DefaultMessages:deleteAccountSuccess'))
      );
      dispatch(actionCreators.deleteAccountSuccess(accountId));
      dispatch(actionCreators.getAccountsAsync(user.id, appConfig.shouldUseAccOptimization));
    }
  },
  deleteAccountSuccess: payload => ({ type: actions.DELETE_ACCOUNT_SUCCESS, payload }),
  deleteAccountFailure: () => ({ type: actions.DELETE_ACCOUNT_FAILURE }),
  getQuestions: (accountNumber, signUpInProgress = false) => async dispatch => {
    dispatch({ type: actions.GET_QUESTIONS });
    const response = await Polling.handle(AccountService.getQuestions(accountNumber));
    if (response.error) {
      AlertHandler.error(retrieveErrors(response.error, message('DefaultMessages:getQuestionsFailure')));
      dispatch({ type: actions.GET_QUESTIONS_FAILURE });
      if (signUpInProgress) dispatch(authActions.restartSignUp(6));
    } else {
      dispatch(actionCreators.getQuestionsSuccess(response.data));
    }
  },
  getQuestionsSuccess(questions) {
    return {
      type: actions.GET_QUESTIONS_SUCCESS,
      payload: { questions }
    };
  },
  getRelatedAccounts: (accountNumber, signUpInProgress = false) => async dispatch => {
    dispatch({ type: actions.GET_RELATED_ACCOUNTS });
    const response = await Polling.handle(AccountService.getRelatedAccounts(accountNumber));
    if (response.error) {
      AlertHandler.error(
        retrieveErrors(response.error, message('DefaultMessages:getRelatedAccountsFailure'))
      );
      dispatch(push(ROUTES.ACCOUNT_STATUS));
    } else {
      dispatch(actionCreators.getRelatedAccountsSuccess(response.data.accounts));
      if (signUpInProgress && !appConfig.signUpFlow.AccountRelationshipValidation) {
        dispatch(actionCreators.addAccount(accountNumber));
      }
    }
  },
  getRelatedAccountsSuccess(accounts) {
    return {
      type: actions.GET_RELATED_ACCOUNTS_SUCCESS,
      payload: accounts
    };
  },
  getTermsAndConditions() {
    return async dispatch => {
      dispatch({ type: actions.GET_TERMS_AND_CONDITIONS });
      const response = await AccountService.getTermsAndConditions();

      if (response.ok) {
        dispatch(actionCreators.getTermsAndConditionsSuccess(response.data.content));
      } else {
        AlertHandler.error(
          retrieveErrors(response.data, message('DefaultMessages:getTermsAndConditionsFailure'))
        );
      }
    };
  },
  getTermsAndConditionsSuccess(terms) {
    return {
      type: actions.GET_TERMS_AND_CONDITIONS_SUCCESS,
      payload: { terms }
    };
  },
  cleanAccountNewNotifications() {
    return {
      type: actions.CLEAN_ACCOUNT_NEW_NOTIFICATIONS,
      payload: 0
    };
  },
  clearCurrentAccount() {
    return { type: actions.CLEAR_CURRENT_ACCOUNT };
  },
  clearSocialRateStatus() {
    return { type: actions.CLEAR_SOCIAL_RATE_STATUS };
  },
  validateSocialRate: (clientNumber, isPublic) => async dispatch => {
    dispatch({ type: actions.VALIDATE_SOCIAL_RATE });
    const response = await Polling.handle(AccountService.validateSocialRate(clientNumber));
    if (response.error) {
      AlertHandler.error(
        retrieveErrors(response.error, message('DefaultMessages:validateSocialRateFailure'))
      );
      dispatch({ type: actions.VALIDATE_SOCIAL_RATE_FAILURE });
    } else {
      dispatch(actionCreators.validateSocialRateSuccess(response.data, isPublic, clientNumber));
    }
  },
  validateSocialRateSuccess(socialRateStatus, isPublic, clientNumber) {
    return {
      type: actions.VALIDATE_SOCIAL_RATE_SUCCESS,
      payload: { socialRateStatus, isPublic, clientNumber }
    };
  },
  getAccountBalance: (clientNumber, email) => async (dispatch, getState) => {
    dispatch({ type: actions.GET_ACCOUNT_BALANCE });
    const response = await Polling.handle(AccountService.getAccountBalance(clientNumber, email));

    if (response.error) {
      dispatch(
        alertActions.openModal({
          content: retrieveErrors(response.error, message('DefaultMessages:getAccountBalanceFailure')),
          title: message('PublicBalanceQuery:resultModalTitle')
        })
      );
      dispatch(actionCreators.getAccountBalanceFailure(response.error));
    } else {
      dispatch(actionCreators.getAccountBalanceSuccess(response.data));
      dispatch(
        alertActions.openModal({
          content: getState().account.accountBalance.message,
          title: message('PublicBalanceQuery:resultModalTitle')
        })
      );
    }
  },
  getAccountBalanceSuccess: balance => ({
    type: actions.GET_ACCOUNT_BALANCE_SUCCESS,
    payload: balance
  }),
  getAccountBalanceFailure: error => ({
    type: actions.GET_ACCOUNT_BALANCE_FAILURE,
    payload: { error }
  }),
  resetAccountBalance: () => ({
    type: actions.RESET_ACCOUNT_BALANCE
  }),
  getTermsOfService: service => async dispatch => {
    dispatch({ type: actions.GET_TERMS_OF_SERVICE });
    const response = await Polling.handle(AccountService.getTermsOfService(service));
    if (response.error) {
      AlertHandler.error(retrieveErrors(response.error, message('DefaultMessages:getTermsOfServiceFailure')));
      dispatch(actionCreators.getTermsOfServiceFailure(response.error));
    } else {
      dispatch(actionCreators.getTermsOfServiceSuccess(response.data.content));
    }
  },
  getTermsOfServiceSuccess: balance => ({ type: actions.GET_TERMS_OF_SERVICE_SUCCESS, payload: balance }),
  getTermsOfServiceFailure: error => ({ type: actions.GET_TERMS_OF_SERVICE_FAILURE, payload: { error } }),
  acceptServiceAgreement: (service, accounts, firstAccount) => async (dispatch, getState) => {
    dispatch({ type: actions.ACCEPT_SERVICE_AGREEMENT });
    const { signUpInProgress } = getState().auth.signUpFlow;
    const { id } = getState().auth.currentUser;
    const response = await Polling.handle(AccountService.acceptServiceAgreement(service, accounts));
    if (response.error) {
      AlertHandler.error(
        retrieveErrors(response.error, message('DefaultMessages:acceptServiceAgreementFailure'))
      );
      dispatch(actionCreators.acceptServiceAgreementFailure(response.error));
    } else {
      AlertHandler.success(
        retrieveSuccessMessage(response.data, message('DefaultMessages:acceptServiceAgreementSuccess'))
      );
      dispatch(actionCreators.acceptServiceAgreementSuccess(response.data));
      if (signUpInProgress) {
        if (appConfig.signUpFlow.RelatedAccounts && firstAccount) {
          dispatch(authActions.updateSignUpStep());
        } else {
          dispatch(push(ROUTES.ACCOUNT_STATUS));
        }
      } else {
        dispatch(actionCreators.getAccountsAsync(id, false));
      }
    }
  },
  acceptServiceAgreementSuccess: result => ({
    type: actions.ACCEPT_SERVICE_AGREEMENT_SUCCESS,
    payload: result
  }),
  acceptServiceAgreementFailure: error => ({
    type: actions.ACCEPT_SERVICE_AGREEMENT_FAILURE,
    payload: { error }
  }),
  addAccountInit: () => ({
    type: actions.ADD_ACCOUNT_INIT
  }),
  addAccountUpdateStep: stepNumber => ({
    type: actions.ADD_ACCOUNT_UPDATE_STEP,
    payload: stepNumber
  }),
  addAccountSetInitialAccount: clientNumber => ({
    type: actions.ADD_ACCOUNT_SET_INITIAL_ACCOUNT,
    payload: clientNumber
  }),
  addAccountFinish: () => ({
    type: actions.ADD_ACCOUNT_FINISH
  }),
  showedRelatedSuppliesModal: () => ({
    type: actions.SHOWED_RELATED_SUPPLIES
  }),
  evaluateChangeExpirationDate: () => async dispatch => {
    dispatch({ type: actions.EVALUATE_CHANGE_EXP_DATE });
    const response = await Polling.handle(AccountService.evaluateChangeExpirationDate());
    if (response.error) {
      AlertHandler.error(
        retrieveErrors(response.error, message('DefaultMessages:evaluateChangeExpirationDateFailure'))
      );
      dispatch(actionCreators.evaluateChangeExpirationDateFailure(response.error));
    } else {
      if (response.data.message && !response.data.can_view)
        AlertHandler.info(retrieveSuccessMessage(response.data, response.data.message));
      dispatch(actionCreators.evaluateChangeExpirationDateSuccess(response.data));
    }
  },
  evaluateChangeExpirationDateSuccess: result => ({
    type: actions.EVALUATE_CHANGE_EXP_DATE_SUCCESS,
    payload: result
  }),
  evaluateChangeExpirationDateFailure: error => ({
    type: actions.EVALUATE_CHANGE_EXP_DATE_FAILURE,
    payload: { error }
  }),
  changeExpirationDate: (startDay, endDay) => async dispatch => {
    dispatch({ type: actions.CHANGE_EXPIRATION_DATE });
    const response = await Polling.handle(AccountService.changeExpirationDate(startDay, endDay));
    if (response.error) {
      AlertHandler.error(
        retrieveErrors(response.error, message('DefaultMessages:changeExpirationDateFailure'))
      );
      dispatch(actionCreators.changeExpirationDateFailure(response.error));
    } else {
      dispatch(actionCreators.changeExpirationDateSuccess(response.data));
      AlertHandler.success(
        retrieveSuccessMessage(response.data, message('DefaultMessages:changeExperationDateSuccess'))
      );
      dispatch(goBack());
    }
  },
  changeExpirationDateSuccess: result => ({ type: actions.CHANGE_EXPIRATION_DATE_SUCCESS, payload: result }),
  changeExpirationDateFailure: error => ({ type: actions.CHANGE_EXPIRATION_DATE_FAILURE, payload: { error } })
};

/* ------------- Account reducer ------------- */

const defaultState = {
  accounts: [],
  accountsLoading: false,
  accountsError: null,
  addAccountLoading: false,
  relatedAccountLoading: false,
  currentAccount: null,
  questions: [],
  questionsLoading: false,
  relatedAccounts: [],
  terms: '',
  currentBalance: null,
  termsLoading: false,
  socialRateLoading: false,
  socialRateStatus: null,
  accountBalanceLoading: false,
  accountBalance: null,
  addAccountFlow: {
    inProgress: false,
    currentStep: 0,
    initialAccount: null,
    initialAccountAdded: false,
    relatedAccountsAdded: false
  },
  relatedAccountsAlertDisplayed: false,
  evaluateChangeExpDate: null,
  evaluateChangeExpDateLoading: false,
  evaluateChangeExpDateError: null,
  changeExpirationDate: null,
  changeExpirationDateLoading: false,
  changeExpirationDateError: null,
  alerts: [],
  positiveCreditStatus: null,
  positiveCreditStatusLoading: false,
  positiveCreditStatusError: null
};

/* eslint-disable complexity */
export function reducer(state = Immutable(defaultState), action) {
  switch (action.type) {
    case actions.GET_ACCOUNTS: {
      return Immutable.merge(state, { accountsLoading: true });
    }
    case actions.GET_ACCOUNT_ALERTS: {
      return Immutable.merge(state, { alerts: [], alertsLoading: true });
    }
    case actions.GET_ACCOUNT_ALERTS_SUCCESS: {
      return Immutable.merge(state, { alerts: action.payload, alertsLoading: false, alertsOpen: true });
    }
    case actions.CLOSE_ACCOUNT_ALERTS: {
      return Immutable.merge(state, { alerts: action.payload });
    }
    case actions.GET_ACCOUNT_ALERTS_FAILURE: {
      return Immutable.merge(state, { alertsLoading: false });
    }
    case actions.GET_ACCOUNTS_SUCCESS: {
      return Immutable.merge(state, { accountsLoading: false, accounts: action.payload });
    }
    case actions.GET_ACCOUNTS_FAILURE: {
      return Immutable.merge(state, { accountsLoading: false, accounts: [], accountsError: action.payload });
    }
    case actions.GET_TERMS_AND_CONDITIONS: {
      return Immutable.merge(state, { termsLoading: true });
    }
    case actions.GET_TERMS_AND_CONDITIONS_SUCCESS: {
      return Immutable.merge(state, { terms: action.payload.terms, termsLoading: false });
    }
    case actions.GET_POSITIVE_CREDIT_STATUS: {
      return Immutable.merge(state, { positiveCreditStatusLoading: true });
    }
    case actions.GET_POSITIVE_CREDIT_STATUS_SUCCESS: {
      return Immutable.merge(state, {
        positiveCreditStatusLoading: false,
        positiveCreditStatus: action.payload
      });
    }
    case actions.GET_POSITIVE_CREDIT_STATUS_FAILURE: {
      return Immutable.merge(state, {
        positiveCreditStatusLoading: false,
        positiveCreditStatusErrors: action.payload
      });
    }
    case actions.CLEAR_POSITIVE_CREDIT_STATUS: {
      return Immutable.merge(state, {
        positiveCreditStatus: null
      });
    }

    case actions.ADD_ACCOUNTS: {
      return Immutable.merge(state, { addAccountLoading: true });
    }
    case actions.ADD_ACCOUNTS_SUCCESS:
    case actions.ADD_ACCOUNTS_FAILURE: {
      return Immutable.merge(state, { addAccountLoading: false });
    }
    case actions.CHANGE_CURRENT_ACCOUNT: {
      return Immutable.merge(state, { currentAccount: action.payload.account });
    }
    case actions.DELETE_ACCOUNT: {
      return Immutable.merge(state, { deleteAccountLoading: true });
    }
    case actions.DELETE_ACCOUNT_SUCCESS: {
      return Immutable.merge(state, { deleteAccountLoading: false });
    }
    case actions.DELETE_ACCOUNT_FAILURE: {
      return Immutable.merge(state, { deleteAccountLoading: false });
    }
    case actions.FETCH_ACCOUNTS_SUCCESS: {
      return Immutable.merge(state, { accounts: action.payload.accounts, loading: false });
    }
    case actions.APPEND_ACCOUNTS_SUCCESS: {
      return Immutable.merge(state, {
        accounts: [...state.accounts, ...action.payload],
        appendAccountsLoading: false
      });
    }
    case actions.APPEND_ACCOUNTS_FAILURE: {
      return Immutable.merge(state, { appendAccountsLoading: false, appendAccountsError: action.payload });
    }
    case actions.APPEND_ACCOUNTS: {
      return Immutable.merge(state, { appendAccountsLoading: true });
    }
    case actions.GET_QUESTIONS: {
      return Immutable.merge(state, { questionsLoading: true });
    }
    case actions.GET_QUESTIONS_SUCCESS: {
      return Immutable.merge(state, { questions: action.payload.questions, questionsLoading: false });
    }
    case actions.GET_QUESTIONS_FAILURE: {
      return Immutable.merge(state, { questions: [], questionsLoading: false });
    }
    case actions.GET_RELATED_ACCOUNTS: {
      return Immutable.merge(state, { relatedAccounts: null, relatedAccountLoading: true });
    }
    case actions.GET_RELATED_ACCOUNTS_SUCCESS: {
      return Immutable.merge(state, { relatedAccounts: action.payload, relatedAccountLoading: false });
    }
    case actions.CLEAN_ACCOUNT_NEW_NOTIFICATIONS: {
      return Immutable.merge(state, {
        currentAccount: {
          ...state.currentAccount,
          new_notifications: action.payload
        }
      });
    }
    case actions.SHOWED_RELATED_SUPPLIES: {
      return Immutable.merge(state, {
        relatedAccountsAlertDisplayed: true
      });
    }
    case actions.VALIDATE_SOCIAL_RATE: {
      return Immutable.merge(state, {
        socialRateLoading: true,
        socialRateStatus: null
      });
    }
    case actions.VALIDATE_SOCIAL_RATE_SUCCESS: {
      return Immutable.merge(state, {
        socialRateLoading: false,
        socialRateStatus: { ...action.payload.socialRateStatus, clientNumber: action.payload.clientNumber }
      });
    }
    case actions.VALIDATE_SOCIAL_RATE_FAILURE: {
      return Immutable.merge(state, {
        socialRateLoading: false,
        socialRateStatus: null
      });
    }
    case actions.CLEAR_CURRENT_ACCOUNT: {
      return Immutable.merge(state, {
        currentAccount: null
      });
    }
    case actions.CLEAR_SOCIAL_RATE_STATUS: {
      return Immutable.merge(state, {
        socialRateStatus: null
      });
    }
    case actions.GET_ACCOUNT_ASYNC: {
      return Immutable.merge(state, {
        accountsLoading: true
      });
    }
    case actions.GET_ACCOUNT_ASYNC_FAILURE: {
      return Immutable.merge(state, {
        accountsLoading: false
      });
    }
    case actions.GET_ACCOUNT_ASYNC_SUCCESS: {
      const accountIndex = state.accounts.findIndex(account => account.id === action.payload.id);
      return Immutable.merge(state, {
        accounts: accountIndex
          ? state.accounts
              .slice(0, accountIndex)
              .concat({
                ...action.payload
              })
              .concat(state.accounts.slice(accountIndex + 1))
          : state.accounts,
        currentAccount:
          state.currentAccount && state.currentAccount.id === action.payload.id
            ? { ...state.currentAccount, ...action.payload }
            : state.currentAccount,
        accountsLoading: false
      });
    }
    case actions.GET_ACCOUNT_BALANCE: {
      return Immutable.merge(state, {
        accountBalanceLoading: true,
        accountBalance: null
      });
    }
    case actions.GET_ACCOUNT_BALANCE_SUCCESS: {
      return Immutable.merge(state, {
        accountBalanceLoading: false,
        accountBalance: action.payload
      });
    }
    case actions.GET_ACCOUNT_BALANCE_FAILURE: {
      return Immutable.merge(state, {
        accountBalanceLoading: false,
        accountBalance: null
      });
    }
    case actions.GET_TERMS_OF_SERVICE: {
      return Immutable.merge(state, {
        termsOfServiceLoading: true,
        termsOfService: null
      });
    }
    case actions.GET_TERMS_OF_SERVICE_SUCCESS: {
      return Immutable.merge(state, {
        termsOfServiceLoading: false,
        termsOfService: action.payload
      });
    }
    case actions.GET_TERMS_OF_SERVICE_FAILURE: {
      return Immutable.merge(state, {
        termsOfServiceLoading: false,
        termsOfService: null
      });
    }
    case actions.ACCEPT_SERVICE_AGREEMENT: {
      return Immutable.merge(state, {
        acceptServiceAgreementLoading: true,
        acceptServiceAgreement: null
      });
    }
    case actions.ACCEPT_SERVICE_AGREEMENT_SUCCESS: {
      return Immutable.merge(state, { acceptServiceAgreementLoading: false });
    }
    case actions.ACCEPT_SERVICE_AGREEMENT_FAILURE: {
      return Immutable.merge(state, { acceptServiceAgreementLoading: false });
    }
    case actions.RESET_ACCOUNT_BALANCE: {
      return Immutable.merge(state, {
        accountBalanceLoading: false,
        accountBalance: null
      });
    }
    case actions.ACCOUNT_UPDATED: {
      return Immutable.merge(state, {
        currentAccount: {
          ...state.currentAccount,
          accountUpdated: true
        }
      });
    }
    case actions.BILLS_UPDATED: {
      return Immutable.merge(state, {
        currentAccount: {
          ...state.currentAccount,
          billsUpdated: true
        }
      });
    }
    case actions.CLAIMS_UPDATED: {
      return Immutable.merge(state, {
        currentAccount: {
          ...state.currentAccount,
          claimsUpdated: true
        }
      });
    }
    case actions.ADD_ACCOUNT_INIT: {
      return Immutable.merge(state, {
        addAccountFlow: {
          ...state.addAccountFlow,
          inProgress: true
        }
      });
    }
    case actions.ADD_ACCOUNT_UPDATE_STEP: {
      return Immutable.merge(state, {
        addAccountFlow: {
          ...state.addAccountFlow,
          currentStep: action.payload
        }
      });
    }
    case actions.ADD_ACCOUNT_SET_INITIAL_ACCOUNT: {
      return Immutable.merge(state, {
        addAccountFlow: {
          ...state.addAccountFlow,
          initialAccount: action.payload
        }
      });
    }
    case actions.ADD_ACCOUNT_FINISH: {
      return Immutable.merge(state, {
        addAccountFlow: {
          inProgress: false,
          currentStep: 0,
          initialAccount: null,
          initialAccountAdded: false
        }
      });
    }
    case actions.ADD_ACCOUNT_INITIAL_ACCOUNT_ADDED: {
      return Immutable.merge(state, {
        addAccountFlow: {
          ...state.addAccountFlow,
          initialAccountAdded: true
        }
      });
    }
    case actions.ADD_ACCOUNT_RELATED_ACCOUNTS_ADDED: {
      return Immutable.merge(state, {
        addAccountFlow: {
          ...state.addAccountFlow,
          relatedAccountsAdded: true
        }
      });
    }
    case actions.EVALUATE_CHANGE_EXP_DATE: {
      return Immutable.merge(state, {
        evaluateChangeExpDateLoading: true,
        evaluateChangeExpDate: null
      });
    }
    case actions.EVALUATE_CHANGE_EXP_DATE_SUCCESS: {
      return Immutable.merge(state, {
        evaluateChangeExpDateLoading: false,
        evaluateChangeExpDate: action.payload
      });
    }
    case actions.EVALUATE_CHANGE_EXP_DATE_FAILURE: {
      return Immutable.merge(state, {
        evaluateChangeExpDateLoading: false,
        evaluateChangeExpDateError: action.payload
      });
    }
    case actions.CHANGE_EXPIRATION_DATE: {
      return Immutable.merge(state, {
        changeExpirationDateLoading: true,
        changeExpirationDate: null
      });
    }
    case actions.CHANGE_EXPIRATION_DATE_SUCCESS: {
      return Immutable.merge(state, {
        changeExpirationDateLoading: false,
        changeExpirationDate: action.payload
      });
    }
    case actions.CHANGE_EXPIRATION_DATE_FAILURE: {
      return Immutable.merge(state, {
        changeExpirationDateLoading: false,
        changeExpirationDateError: action.payload
      });
    }
    default: {
      return state;
    }
  }
}
/* eslint-enable complexity */

/* ------------- Account propTypes ------------- */

export const accountPropTypes = PropTypes.shape({
  id: PropTypes.number.isRequired,
  client_number: PropTypes.string.isRequired,
  address: PropTypes.string.isRequired,
  holder_name: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  adherence_to_automatic_debit: PropTypes.string.isRequired,
  adherence_to_digital_bill: PropTypes.string.isRequired,
  new_notifications: PropTypes.number.isRequired,
  digital_bill_adherence_form_id: PropTypes.number,
  automatic_debit_adherence_form_id: PropTypes.number,
  complaint_form_id: PropTypes.number
});
